import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle } from "./AdminLayout";
import { Switch } from "../../components/ui/switch";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple";
const labelCls = "text-xs uppercase tracking-[0.15em] text-white/50 block mb-1.5";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);

  const load = () => api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!s) return <p className="text-white/40 font-mono text-sm">Loading…</p>;

  const save = async (patch, msg = "Settings saved") => {
    try {
      await api.put("/admin/settings", patch);
      toast.success(msg);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-settings">
      <PageTitle title="Global Settings" sub="Bot configuration, feature toggles and integrations." />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="rounded-lg border border-white/5 bg-panel p-6 space-y-5" data-testid="settings-bot">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Bot configuration</p>
          <div>
            <label className={labelCls}>Version</label>
            <input value={s.bot.version} onChange={(e) => setS({ ...s, bot: { ...s.bot, version: e.target.value } })} className={`${inputCls} font-mono`} data-testid="settings-version" />
          </div>
          <div>
            <label className={labelCls}>Activity / presence message</label>
            <input value={s.bot.activity} onChange={(e) => setS({ ...s, bot: { ...s.bot, activity: e.target.value } })} className={inputCls} data-testid="settings-activity" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Shards</label>
              <input type="number" value={s.bot.shards} onChange={(e) => setS({ ...s, bot: { ...s.bot, shards: Number(e.target.value) } })} className={inputCls} data-testid="settings-shards" />
            </div>
            <div>
              <label className={labelCls}>Active voice connections</label>
              <input type="number" value={s.bot.active_voice} onChange={(e) => setS({ ...s, bot: { ...s.bot, active_voice: Number(e.target.value) } })} className={inputCls} data-testid="settings-voice" />
            </div>
          </div>
          <button onClick={() => save({ bot: s.bot }, "Bot config saved")} className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-7 py-2.5 rounded-full text-sm transition-colors duration-150" data-testid="settings-save-bot">Save bot config</button>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="settings-toggles">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Feature toggles</p>
            <div className="space-y-3">
              {[["review_submissions", "Public review submissions"], ["support_tickets", "Public support tickets"], ["premium_page", "Premium page visible"]].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3 text-sm text-white/70 cursor-pointer" data-testid={`toggle-${k}`}>
                  {label}
                  <Switch checked={!!s.feature_toggles?.[k]} onCheckedChange={(v) => save({ feature_toggles: { ...s.feature_toggles, [k]: v } }, `${label} ${v ? "enabled" : "disabled"}`)} />
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-6" data-testid="settings-maintenance">
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-400/80 font-semibold mb-3">Maintenance mode</p>
            <p className="text-sm text-white/60 mb-4">Shown on the status page while you work on things.</p>
            <Switch checked={!!s.maintenance_mode} onCheckedChange={(v) => save({ maintenance_mode: v }, `Maintenance mode ${v ? "ON" : "OFF"}`)} data-testid="toggle-maintenance" />
          </div>

          <div className="rounded-lg border border-white/5 bg-panel p-6 space-y-4" data-testid="settings-volume-caps">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Volume limits</p>
            <p className="text-sm text-white/60">Max !volume percentage. The bot picks changes up within 5 minutes.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Free users max %</label>
                <input type="number" min="1" max="500" value={s.volume_caps?.free ?? 100} onChange={(e) => setS({ ...s, volume_caps: { ...s.volume_caps, free: Number(e.target.value) } })} className={inputCls} data-testid="settings-volume-free" />
              </div>
              <div>
                <label className={labelCls}>Premium max %</label>
                <input type="number" min="1" max="500" value={s.volume_caps?.premium ?? 200} onChange={(e) => setS({ ...s, volume_caps: { ...s.volume_caps, premium: Number(e.target.value) } })} className={inputCls} data-testid="settings-volume-premium" />
              </div>
            </div>
            <button onClick={() => save({ volume_caps: s.volume_caps }, "Volume limits saved")} className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-7 py-2.5 rounded-full text-sm transition-colors duration-150" data-testid="settings-save-volume">Save volume limits</button>
          </div>
        </div>
      </div>
    </div>
  );
}
